import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Header } from '../components';
import { FiUser, FiUsers, FiArrowLeft, FiPlus, FiTrash2, FiVideo, FiCheckSquare, FiArrowRight, FiSearch} from 'react-icons/fi';
import { useStateContext } from '../contexts/ContextProvider';
import axios from 'axios';

const BatchView = () => {
  const { batchId } = useParams();
  const navigate = useNavigate();
  const { currentColor } = useStateContext();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [batch, setBatch] = useState(null);
  const [contentDetails, setContentDetails] = useState(null);
  const [students, setStudents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [availableTeachers, setAvailableTeachers] = useState([]);
  const [availableStudents, setAvailableStudents] = useState([]);
  const [selectedTeachers, setSelectedTeachers] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [teacherDropdownOpen, setTeacherDropdownOpen] = useState(false);
  const [studentDropdownOpen, setStudentDropdownOpen] = useState(false);
  const [studentsWithAccess, setStudentsWithAccess] = useState([]);
  const [userRole, setUserRole] = useState('');
  const [isTeacher, setIsTeacher] = useState(false);
  const [isAdminOrCounsellor, setIsAdminOrCounsellor] = useState(false);
  const [studentSearchTerm, setStudentSearchTerm] = useState('');
  const API_BASE_URL = 'https://apissc.sainikschoolcadet.com/api';

  // Fetch batch details and related data
  // Update the useEffect hook to properly handle API calls and dependencies
  useEffect(() => {
    // Get the role from localStorage
    const storedRole = localStorage.getItem('role');
    setUserRole(storedRole || '');
    setIsTeacher(storedRole === 'teacher');
    setIsAdminOrCounsellor(storedRole === 'admin' || storedRole === 'counsellor');
  }, []);
// 1. First useEffect with improved StudentContentAccess API call
useEffect(() => {
  const fetchBatchData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Fetching batch data for ID:', batchId);
      
      // Fetch batch details
      const batchResponse = await axios.get(`${API_BASE_URL}/Batches/${batchId}`);
      console.log('Batch data received:', batchResponse.data);
      setBatch(batchResponse.data);
      
      // Fetch content details
      if (batchResponse.data.content_id) {
        console.log('Content ID found:', batchResponse.data.content_id);
        const contentResponse = await axios.get(`${API_BASE_URL}/Contents/${batchResponse.data.content_id}`);
        console.log('Content data received:', contentResponse.data);
        setContentDetails(contentResponse.data);
        
        // Fetch students with access to this content via StudentContentAccess API
        try {
          console.log('Fetching student content access for content ID:', batchResponse.data.content_id);
          // Changed to explicit try/catch to prevent errors here from stopping the rest of the function
          const studentAccessResponse = await axios.get(`${API_BASE_URL}/StudentContentAccess/content/${batchResponse.data.content_id}`);
          console.log('Student access response received:', studentAccessResponse.data);
          
          if (studentAccessResponse.data) {
            // Extract student IDs - handle different possible response structures
            let studentIds = [];
            
            if (Array.isArray(studentAccessResponse.data.student_ids)) {
              // Standard format: { student_ids: [1, 2, 3] }
              studentIds = studentAccessResponse.data.student_ids;
              console.log('Found student_ids array with length:', studentIds.length);
            } 
            else if (Array.isArray(studentAccessResponse.data)) {
              // Array of objects format
              studentIds = studentAccessResponse.data
                .map(item => item.student_id || item.s_id)
                .filter(id => id != null);
              console.log('Extracted IDs from array of objects:', studentIds);
            }
            else {
              console.log('Student access response structure:', Object.keys(studentAccessResponse.data));
              // Try to find any array property that might contain the student IDs
              Object.keys(studentAccessResponse.data).forEach(key => {
                if (Array.isArray(studentAccessResponse.data[key])) {
                  console.log(`Found array in response at key '${key}':`, studentAccessResponse.data[key]);
                }
              });
            }
            
            console.log('Final student IDs with access:', studentIds);
            setStudentsWithAccess(studentIds);
          } else {
            console.log('No data in student access response');
            setStudentsWithAccess([]);
          }
        } catch (accessError) {
          console.error('Error fetching student content access:', accessError);
          setStudentsWithAccess([]);
        }
      } else {
        console.log('No content ID found for this batch');
      }
      
      // Fetch students in batch
      await fetchStudents();
      
      // Fetch teachers assigned to batch
      await fetchTeachers();
      
      // Fetch available teachers
      await fetchAvailableTeachers();
    } catch (err) {
      console.error('Error fetching batch data:', err);
      setError('Failed to load batch details. Please try again later.');
    } finally {
      setLoading(false);
    }
  };
  
  fetchBatchData();
}, [batchId]); // Only depend on batchId

// 2. Second useEffect with modified dependencies and explicit call to fetch available data
useEffect(() => {
  const updateAvailableLists = async () => {
    // Only proceed if batch data is loaded and not in loading state
    if (!loading && batch) {
      console.log('Updating available students and teachers lists...');
      // Explicitly call these functions
      await fetchAvailableStudents();
      await fetchAvailableTeachers();
    }
  };
  
  updateAvailableLists();
  // Include all necessary dependencies to ensure this effect runs when they change
}, [loading, batch, students.length, teachers.length, studentsWithAccess.length]);

// 3. Add a direct API testing function to check if the endpoint works
// You can call this function from the console for debugging
window.testStudentContentAccess = async (contentId) => {
  try {
    console.log('Direct API test - Fetching student access for content ID:', contentId);
    const response = await axios.get(`${API_BASE_URL}/StudentContentAccess/content/${contentId}`);
    console.log('Direct API test - Response:', response.data);
    return response.data;
  } catch (error) {
    console.error('Direct API test - Error:', error);
    throw error;
  }
};

  const fetchAvailableTeachers = async () => {
    try {
      // Get all teachers
      const response = await axios.get(`${API_BASE_URL}/Users/getByRole/teacher`);
      
      if (response.data) {
        // Get IDs of teachers already assigned to this batch
        const existingTeacherIds = teachers.map(teacher => parseInt(teacher.id));
        
        // Filter out teachers that are already assigned to this batch
        const filteredTeachers = response.data.filter(teacher => 
          !existingTeacherIds.includes(teacher.u_id)
        );
        
        setAvailableTeachers(filteredTeachers);
      }
    } catch (error) {
      console.error('Error fetching available teachers:', error);
    }
  };

  const fetchStudents = async () => {
    try {
      if (!batchId) {
        console.warn('batchId is undefined or null. Aborting fetch.');
        return;
      }
  
      const apiUrl = `${API_BASE_URL}/BatchStudents/batch/${batchId}/students`;
      console.log('Calling API:', apiUrl);
  
      const batchResponse = await axios.get(apiUrl);
      console.log('API Response:', batchResponse.data);
  
      const studentsList = batchResponse.data?.students;
  
      if (Array.isArray(studentsList) && studentsList.length > 0) {
        const studentData = await Promise.all(
          studentsList.map(async (studentObj) => {
            try {
              const studentUrl = `${API_BASE_URL}/Students/getbyid/${studentObj.student_id}`;
              const studentResponse = await axios.get(studentUrl);
              const studentDetails = studentResponse.data;
  
              return {
                id: studentObj.student_id,
                name: studentDetails?.f_name || 'Unknown',
                email: studentDetails?.email || 'No email',
                phone: studentDetails?.phonenumber || 'No phone',
                added_by_name: studentObj.added_by_name || 'N/A',
                joined_at: studentObj.joined_at
                  
              };
              
            } catch (error) {
              console.error(`Failed to fetch student ${studentObj.student_id}:`, error);
              return null;
            }
          })
        );
  
        const filteredStudents = studentData.filter(Boolean);
        console.log('Final student data:', filteredStudents);
        setStudents(filteredStudents);
      } else {
        console.warn('No students found in batch or invalid structure:', batchResponse.data);
        setStudents([]);
      }
    } catch (error) {
      console.error('Error fetching students:', error);
      console.error('Error details:', error.response?.data || error.message);
      setStudents([]);
    }
  };
  
  

const fetchAvailableStudents = async () => {
  console.log('Fetching available students...');
  console.log('Students with access:', studentsWithAccess);
  
  // Get current students in the batch (as IDs only)
  const currentStudentIds = students.map(student => Number(student.id));
  console.log('Current students in batch (IDs):', currentStudentIds);
  
  if (!studentsWithAccess || studentsWithAccess.length === 0) {
    console.log('No students have access to this content');
    setAvailableStudents([]);
    return;
  }
  
  try {
    // Format conversion - ensure all IDs are numbers for comparison
    const existingStudentIds = currentStudentIds;
    console.log('Existing student IDs in batch:', existingStudentIds);
    
    // Filter students who have access but are not in the batch
    const studentsToFetch = studentsWithAccess
      .filter(studentId => {
        // Convert both to numbers for comparison
        const studentIdNum = Number(studentId);
        return !existingStudentIds.includes(studentIdNum);
      })
      .map(id => String(id)); // Ensure IDs are strings for API calls
    
    console.log('Students to fetch (have access but not in batch):', studentsToFetch);
    
    if (studentsToFetch.length === 0) {
      console.log('All students with access are already in the batch');
      setAvailableStudents([]);
      return;
    }
    
    // Fetch details for each eligible student
    const availableStudentsList = [];
    
    for (const studentId of studentsToFetch) {
      try {
        console.log(`Fetching details for student ID: ${studentId}`);
        const studentResponse = await axios.get(`${API_BASE_URL}/Students/getbyid/${studentId}`);
        
        if (studentResponse.data) {
          console.log(`Student ${studentId} details:`, studentResponse.data);
          availableStudentsList.push({
            s_id: studentId,
            f_name: studentResponse.data.f_name || 'Unknown',
            email: studentResponse.data.email || 'No email',
            phonenumber: studentResponse.data.phonenumber || 'No phone',
            created_at: studentResponse.data.created_at || null
          });
        } else {
          console.log(`No data returned for student ${studentId}`);
        }
      } catch (error) {
        console.error(`Error fetching student ${studentId}:`, error);
      }
    }
    
    // Sort by created_at descending (most recent first), or by student ID descending as fallback
    availableStudentsList.sort((a, b) => {
      if (a.created_at && b.created_at) {
        return new Date(b.created_at) - new Date(a.created_at);
      }
      // Fallback to student ID (assuming higher IDs are more recent)
      return parseInt(b.s_id) - parseInt(a.s_id);
    });
    
    console.log('Final available students list:', availableStudentsList);
    setAvailableStudents(availableStudentsList);
  } catch (error) {
    console.error('Error preparing available students list:', error);
    setAvailableStudents([]);
  }
};

  const fetchTeachers = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/BatchTeachers/batch/${batchId}/teachers`);
      
      if (response.data && response.data.teacher_ids) {
        const teacherData = [];
        
        for (const teacherId of response.data.teacher_ids) {
          try {
            // Use the Users/getbyid endpoint as shown in your example
            const teacherResponse = await axios.get(`${API_BASE_URL}/Users/getbyid/${teacherId}`);
            
            if (teacherResponse.data) {
              teacherData.push({
                id: teacherId,
                name: teacherResponse.data.f_name,
                email: teacherResponse.data.email,
                phone: teacherResponse.data.phonenumber
              });
            }
          } catch (error) {
            console.error(`Error fetching teacher ${teacherId}:`, error);
          }
        }
        
        setTeachers(teacherData);
      }
    } catch (error) {
      console.error('Error fetching teachers:', error);
    }
  };

  const addSelectedStudents = async () => {
    if (selectedStudents.length === 0) {
      alert('Please select students to add');
      return;
    }
  
    try {
      // Get user info from localStorage
      const added_by_name = localStorage.getItem('name');
      const added_by_u_id = localStorage.getItem('user_id');
  
      if (!added_by_name || !added_by_u_id) {
        alert('User info missing from localStorage.');
        return;
      }
  
      // Create an array of promises for all student additions
      const addPromises = selectedStudents.map(studentId => 
        axios.post(`${API_BASE_URL}/BatchStudents`, {
          batch_id: batchId,
          s_id: studentId,
          added_by_name,
          added_by_u_id
        })
      );
  
      // Wait for all promises to resolve
      await Promise.all(addPromises);
  
      alert(`${selectedStudents.length} student(s) added successfully`);
      setSelectedStudents([]);
      setStudentDropdownOpen(false);
      setStudentSearchTerm(''); // Clear search term
      await fetchStudents();
      await fetchAvailableStudents();
    } catch (error) {
      console.error('Error adding students:', error.response?.data || error.message);
      alert(`Failed to add students: ${error.response?.data?.message || error.message}`);
    }
  };
  
  
  const toggleStudentSelection = (studentId) => {
    if (selectedStudents.includes(studentId)) {
      setSelectedStudents(selectedStudents.filter(id => id !== studentId));
    } else {
      setSelectedStudents([...selectedStudents, studentId]);
    }
  };

  const removeStudent = async (studentId) => {
    try {
      // First, find the batch-student record by batch_id and student_id
      const batchStudentsResponse = await axios.get(`${API_BASE_URL}/BatchStudents`);
      const batchStudentRecord = batchStudentsResponse.data.find(
        record => record.batch_id == batchId && (record.s_id == studentId || record.student_id == studentId)
      );
      
      if (!batchStudentRecord) {
        alert('Student-batch association not found');
        return;
      }
      
      // Delete the batch-student record using its ID
      await axios.delete(`${API_BASE_URL}/BatchStudents/${batchStudentRecord.id}`);
      alert('Student removed successfully');
      await fetchStudents();
    } catch (error) {
      console.error('Error removing student:', error);
      alert('Failed to remove student');
    }
  };

  const addSelectedTeachers = async () => {
    if (selectedTeachers.length === 0) {
      alert('Please select teachers to add');
      return;
    }
    
    try {
      // Add each selected teacher to the batch
      for (const teacherId of selectedTeachers) {
        await axios.post(`${API_BASE_URL}/BatchTeachers`, {
          batch_id: batchId,
          teacher_id: teacherId
        });
      }
      
      alert(`${selectedTeachers.length} teacher(s) added successfully`);
      setSelectedTeachers([]);
      setTeacherDropdownOpen(false);
      await fetchTeachers();
      await fetchAvailableTeachers();
    } catch (error) {
      console.error('Error adding teachers:', error);
      alert('Failed to add teachers');
    }
  };
  
  const toggleTeacherSelection = (teacherId) => {
    if (selectedTeachers.includes(teacherId)) {
      setSelectedTeachers(selectedTeachers.filter(id => id !== teacherId));
    } else {
      setSelectedTeachers([...selectedTeachers, teacherId]);
    }
  };

  const removeTeacher = async (teacherId) => {
    try {
      // First, find the batch-teacher record by batch_id and teacher_id
      const batchTeachersResponse = await axios.get(`${API_BASE_URL}/BatchTeachers`, {
        params: {
          batch_id: batchId,
          teacher_id: teacherId
        }
      });
      
      if (!batchTeachersResponse.data.batchTeachers || batchTeachersResponse.data.batchTeachers.length === 0) {
        alert('Teacher-batch association not found');
        return;
      }
      
      const batchTeacherRecord = batchTeachersResponse.data.batchTeachers[0];
      
      // Delete the batch-teacher record using its ID
      await axios.delete(`${API_BASE_URL}/BatchTeachers/${batchTeacherRecord.id}`);
      alert('Teacher removed successfully');
      await fetchTeachers();
    } catch (error) {
      console.error('Error removing teacher:', error);
      alert('Failed to remove teacher');
    }
  };

  // Helper to format dates
  const formatDate = (dateString) => {
    if (!dateString) return 'Not specified';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };
  
  // Handle tab switching
  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  // Go back to batches list
  const handleGoBack = () => {
    if (batch && batch.content_id) {
      navigate(`/batches/${batch.content_id}`);
    } else {
      navigate(-1); // Fallback to browser history
    }
  };


  // Loading state
  if (loading) {
    return (
      <div className="m-2 md:m-10 mt-24 p-2 md:p-10 bg-white rounded-3xl">
        <Header category="Batch" title="Batch Details" />
        <div className="flex justify-center items-center h-64">
          <div className="animate-pulse flex flex-col items-center">
            <div className="w-12 h-12 rounded-full bg-gray-200 mb-3"></div>
            <p className="text-xl text-gray-600">Loading batch details...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="m-2 md:m-10 mt-24 p-2 md:p-10 bg-white rounded-3xl">
        <Header category="Batch" title="Batch Details" />
        <div className="mt-4 p-4 bg-red-50 border border-red-200 text-red-600 rounded-md">
          {error}
        </div>
        <button 
          onClick={handleGoBack}
          className="mt-4 px-4 py-2 rounded-md text-white text-sm inline-flex items-center gap-2"
          style={{ backgroundColor: currentColor }}
        >
          <FiArrowLeft /> Back to Batches
        </button>
      </div>
    );
  }

  // If batch not found
  if (!batch) {
    return (
      <div className="m-2 md:m-10 mt-24 p-2 md:p-10 bg-white rounded-3xl">
        <Header category="Batch" title="Batch Details" />
        <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 text-yellow-600 rounded-md">
          Batch not found or has been deleted.
        </div>
        <button 
          onClick={handleGoBack}
          className="mt-4 px-4 py-2 rounded-md text-white text-sm inline-flex items-center gap-2"
          style={{ backgroundColor: currentColor }}
        >
          <FiArrowLeft /> Back to Batches
        </button>
      </div>
    );
  }

  return (
    <div className="m-2 md:m-10 mt-24 p-2 md:p-10 bg-white rounded-3xl">
      {/* Header */}
      <div className="mb-6">
        <div className="flex justify-between items-start">
          <div>
            <button 
              onClick={handleGoBack}
              className="mb-4 flex items-center text-gray-600 hover:text-gray-900"
            >
              <FiArrowLeft className="mr-2" /> Back to Batches
            </button>
            
            <Header 
              title={batch.batch_name || 'Batch Details'} 
            />
            
            {contentDetails && (
              <div className="mt-2 text-gray-600">
                <span>Part of: {contentDetails.title}</span>
              </div>
            )}
          </div>
          
          
        </div>
      </div>
      
      {/* Status badge */}
      <div className="mb-6">
        <span className={`px-3 py-1 text-sm rounded-full ${
          batch.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}>
          {batch.status.charAt(0).toUpperCase() + batch.status.slice(1)}
        </span>
      </div>
      
      {/* Quick stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mb-8">
  <div className="bg-blue-50 p-5 rounded-lg flex flex-col justify-center h-24">
    <div className="flex items-center mb-1">
      <div className="mr-3 text-blue-600">
        <FiUsers size={22} />
      </div>
      <p className="text-lg font-bold text-blue-900">Total Students</p>
    </div>
    <p className="text-sm text-blue-700 pl-9">{students.length} students enrolled</p>
  </div>
  
  <div className="bg-green-50 p-5 rounded-lg flex flex-col justify-center h-24">
    <div className="flex items-center mb-1">
      <div className="mr-3 text-green-600">
        <FiUser size={22} />
      </div>
      <p className="text-lg font-bold text-green-900">Assigned Teachers</p>
    </div>
    <p className="text-sm text-green-700 pl-9">{teachers.length} teachers available</p>
  </div>
  
  <div onClick={() => navigate(`/livebatch/${batchId}`)} className="bg-purple-50 p-5 rounded-lg flex flex-col justify-center cursor-pointer hover:shadow-lg transition-all duration-300 h-24 relative group">
    <div className="flex items-center mb-1">
      <div className="mr-3 text-purple-600">
        <FiVideo size={22} />
      </div>
      <p className="text-lg font-bold text-purple-900">Create Live Class</p>
    </div>
    <div className="flex justify-between items-center">
      <p className="text-sm text-purple-700 pl-9">View Recordings</p>
      <div className="text-purple-600 transform group-hover:translate-x-1 transition-transform duration-300">
        <FiArrowRight size={18} />
      </div>
    </div>
  </div>
  
  <div onClick={() => navigate(`/batchtest/${batchId}`)} className="bg-orange-50 p-5 rounded-lg flex flex-col justify-center cursor-pointer hover:shadow-lg transition-all duration-300 h-24 relative group">
    <div className="flex items-center mb-1">
      <div className="mr-3 text-orange-600">
        <FiCheckSquare size={22} />
      </div>
      <p className="text-lg font-bold text-orange-900">Tests</p>
    </div>
    <div className="flex justify-between items-center">
      <p className="text-sm text-orange-700 pl-9">Manage Tests</p>
      <div className="text-orange-600 transform group-hover:translate-x-1 transition-transform duration-300">
        <FiArrowRight size={18} />
      </div>
    </div>
  </div>
</div>
      
      {/* Tab navigation */}
      <div className="mb-6 border-b">
        <nav className="flex space-x-4">
          <button
            onClick={() => handleTabChange('overview')}
            className={`py-2 px-4 text-sm font-medium ${
              activeTab === 'overview'
                ? 'border-b-2 border-blue-600 text-blue-600'
                : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => handleTabChange('students')}
            className={`py-2 px-4 text-sm font-medium ${
              activeTab === 'students'
                ? 'border-b-2 border-blue-600 text-blue-600'
                : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Students
          </button>
          <button
            onClick={() => handleTabChange('teachers')}
            className={`py-2 px-4 text-sm font-medium ${
              activeTab === 'teachers'
                ? 'border-b-2 border-blue-600 text-blue-600'
                : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Teachers
          </button>
        </nav>
      </div>
      
      {/* Tab content */}
      <div className="mb-8">
        {/* Overview tab */}
        {activeTab === 'overview' && (
          <div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-lg font-medium mb-4">Batch Information</h3>
              <div className="space-y-3">
                <div className="flex">
                  <span className="text-gray-500 w-32">Batch ID:</span>
                  <span className="font-medium">{batch.batch_id}</span>
                </div>
                <div className="flex">
                  <span className="text-gray-500 w-32">Name:</span>
                  <span className="font-medium">{batch.batch_name}</span>
                </div>
                <div className="flex">
                  <span className="text-gray-500 w-32">Status:</span>
                  <span className="font-medium">{batch.status}</span>
                </div>
                <div className="flex">
                  <span className="text-gray-500 w-32">Created:</span>
                  <span className="font-medium">{formatDate(batch.created_at)}</span>
                </div>
                <div className="flex">
                  <span className="text-gray-500 w-32">Last Updated:</span>
                  <span className="font-medium">{formatDate(batch.updated_at)}</span>
                </div>
              </div>
            </div>
            
            {contentDetails && (
              <div className="mt-6 bg-gray-50 p-6 rounded-lg">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium">Course Information</h3>
                  
                </div>
                <div className="space-y-3">
                  <div className="flex">
                    <span className="text-gray-500 w-32">Course Name:</span>
                    <span className="font-medium">{contentDetails.title}</span>
                  </div>
                  <div className="flex">
                    <span className="text-gray-500 w-32">Description:</span>
                    <span className="font-medium">{contentDetails.description || 'No description available'}</span>
                  </div>
                  <div className="flex">
                    <span className="text-gray-500 w-32">Original Price:</span>
                    <span className="font-medium">
                      <span className="line-through text-gray-500">₹{contentDetails.max_price || 0}</span>
                    </span>
                  </div>
                  <div className="flex">
                  <span className="text-gray-500 w-32">Selling Price:</span>
                    <span className="font-medium text-green-600">₹{contentDetails.sell_price || 0}</span>
                    {contentDetails.max_price > contentDetails.sell_price && (
                      <span className="ml-2 px-2 py-0.5 text-xs bg-green-100 text-green-800 rounded-full">
                        {Math.round(((contentDetails.max_price - contentDetails.sell_price) / contentDetails.max_price) * 100)}% OFF
                      </span>
                    )}
                  </div>
                  <div className="flex">
                    <span className="text-gray-500 w-32">Students with Access:</span>
                    <span className="font-medium">{studentsWithAccess.length}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* Students tab */}
        {activeTab === 'students' && (
          <div>
           {!isTeacher && (
  <div className="mb-6">
    <h3 className="text-lg font-medium mb-4">Add Students</h3>
    
    {studentsWithAccess.length > 0 ? (
      <div className="relative">
        <button
          onClick={() => {
            setStudentDropdownOpen(!studentDropdownOpen);
            if (studentDropdownOpen) {
              setStudentSearchTerm(''); // Clear search when closing
            }
          }}
          className="w-full flex justify-between items-center border border-gray-300 rounded-md px-3 py-2 bg-white cursor-pointer"
        >
          <span>{selectedStudents.length > 0 ? `${selectedStudents.length} students selected` : 'Select students with access to add'}</span>
          <span className="ml-2">{studentDropdownOpen ? '▲' : '▼'}</span>
        </button>
        
        {studentDropdownOpen && (
          <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg">
            {/* Search Input */}
            <div className="p-2 border-b border-gray-200">
              <div className="relative">
                <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                <input
                  type="text"
                  value={studentSearchTerm}
                  onChange={(e) => setStudentSearchTerm(e.target.value)}
                  placeholder="Search by name or phone number..."
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            </div>
            
            <div className="max-h-60 overflow-y-auto p-2">
              {availableStudents.length > 0 ? (() => {
                // Filter students based on search term
                const filteredStudents = availableStudents.filter(student => {
                  if (!studentSearchTerm.trim()) return true;
                  const searchLower = studentSearchTerm.toLowerCase();
                  const nameMatch = student.f_name?.toLowerCase().includes(searchLower);
                  const phoneMatch = student.phonenumber?.includes(studentSearchTerm);
                  return nameMatch || phoneMatch;
                });
                
                if (filteredStudents.length === 0) {
                  return (
                    <div className="p-2 text-sm text-gray-500 text-center">
                      No students found matching "{studentSearchTerm}"
                    </div>
                  );
                }
                
                return filteredStudents.map(student => (
                  <div 
                    key={student.s_id} 
                    className="flex items-center p-2 hover:bg-gray-100 cursor-pointer"
                    onClick={() => toggleStudentSelection(student.s_id)}
                  >
                    <input 
                      type="checkbox" 
                      checked={selectedStudents.includes(student.s_id)}
                      readOnly
                      className="mr-2"
                    />
                    <div className="flex-1">
                      <span className="text-sm font-medium">{student.f_name}</span>
                      {student.phonenumber && (
                        <span className="text-xs text-gray-500 ml-2">{student.phonenumber}</span>
                      )}
                    </div>
                  </div>
                ));
              })() : (
                <div className="p-2 text-sm text-gray-500">All students with access are already added to this batch</div>
              )}
            </div>
            
            <div className="p-2 border-t border-gray-300">
              <button
                onClick={addSelectedStudents}
                className="w-full px-4 py-2 rounded-md text-white text-sm"
                style={{ backgroundColor: currentColor }}
                disabled={selectedStudents.length === 0}
              >
                Add Selected Students
              </button>
            </div>
          </div>
        )}
      </div>
    ) : (
      <div className="p-4 bg-yellow-50 text-yellow-800 rounded-md">
        No students have access to this content. Students need access to the content before they can be added to this batch.
      </div>
    )}
  </div>
)}

            
            <h3 className="text-lg font-medium mb-4">Students in Batch ({students.length})</h3>
            
            {students.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
  <thead className="bg-gray-50">
    <tr>
      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">S.No.</th>
      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Student Name</th>
      {isAdminOrCounsellor && (
        <>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
        </>
      )}
      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Added By</th>
      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Joined At</th>
      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
    </tr>
  </thead>
  <tbody className="bg-white divide-y divide-gray-200">
    {students
      .sort((a, b) => {
        // Sort by joined_at date - most recent first (descending order)
        const dateA = a.joined_at ? new Date(a.joined_at) : new Date(0);
        const dateB = b.joined_at ? new Date(b.joined_at) : new Date(0);
        return dateB - dateA;
      })
      .map((student, index) => (
        <tr key={student.id}>
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{index + 1}</td>
          <td className="px-6 py-4 whitespace-nowrap">
            <div className="text-sm font-medium text-gray-900">{student.name}</div>
          </td>
          {isAdminOrCounsellor && (
            <>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-500">{student.email}</div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-500">{student.phone}</div>
              </td>
            </>
          )}
          <td className="px-6 py-4 whitespace-nowrap">
            <div className="text-sm text-gray-500">{student.added_by_name || 'N/A'}</div>
          </td>
          <td className="px-6 py-4 whitespace-nowrap">
            <div className="text-sm text-gray-500">
              {student.joined_at ? new Date(student.joined_at).toLocaleString() : 'N/A'}
            </div>
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-right">
            <button
              onClick={() => !isTeacher && removeStudent(student.id)}
              className={`text-red-600 ${isTeacher ? 'opacity-40 cursor-not-allowed' : 'hover:text-red-900 cursor-pointer'}`}
              disabled={isTeacher}
              title={isTeacher ? "Teachers cannot remove students" : "Remove student"}
            >
              <FiTrash2 />
            </button>
          </td>
        </tr>
      ))}
  </tbody>
</table>
              </div>
            ) : (
              <div className="bg-gray-50 p-8 rounded-lg text-center">
                <div className="flex justify-center mb-4">
                  <div className="p-4 rounded-full bg-gray-200 text-gray-400">
                    <FiUsers size={24} />
                  </div>
                </div>
                <p className="text-gray-600 mb-4">No students have been added to this batch yet.</p>
              </div>
            )}
          </div>
        )}
        
        {/* Teachers tab */}
        {activeTab === 'teachers' && (
  <div>
    {!isTeacher && (
  <div className="mb-6">
    <h3 className="text-lg font-medium mb-4">Add Teachers</h3>
    <div className="relative">
      <button
        onClick={() => setTeacherDropdownOpen(!teacherDropdownOpen)}
        className="w-full flex justify-between items-center border border-gray-300 rounded-md px-3 py-2 bg-white cursor-pointer"
      >
        <span>{
          selectedTeachers.length > 0
            ? `${selectedTeachers.length} teachers selected`
            : 'Select teachers to add'
        }</span>
        <span className="ml-2">{teacherDropdownOpen ? '▲' : '▼'}</span>
      </button>

      {teacherDropdownOpen && (
        <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg">
          <div className="max-h-60 overflow-y-auto p-2">
            {availableTeachers.length > 0 ? (
              availableTeachers.map((teacher) => (
                <div
                  key={teacher.u_id}
                  className="flex items-center p-2 hover:bg-gray-100 cursor-pointer"
                  onClick={() => toggleTeacherSelection(teacher.u_id)}
                >
                  <input
                    type="checkbox"
                    checked={selectedTeachers.includes(teacher.u_id)}
                    readOnly
                    className="mr-2"
                  />
                  <span className="text-sm">{teacher.f_name} </span>
                </div>
              ))
            ) : (
              <div className="p-2 text-sm text-gray-500">
                All teachers are already assigned to this batch
              </div>
            )}
          </div>

          <div className="p-2 border-t border-gray-300">
            <button
              onClick={addSelectedTeachers}
              className="w-full px-4 py-2 rounded-md text-white text-sm"
              style={{ backgroundColor: currentColor }}
              disabled={selectedTeachers.length === 0}
            >
              Add Selected Teachers
            </button>
          </div>
        </div>
      )}
    </div>
  </div>
)}

    <h3 className="text-lg font-medium mb-4">Teachers ({teachers.length})</h3>

    {teachers.length > 0 ? (
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
  <tr>
    <th
      scope="col"
      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
    >
      S.No.
    </th>
    <th
      scope="col"
      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
    >
      Teacher Name
    </th>
    {isAdminOrCounsellor && (
      <>
        <th
          scope="col"
          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
        >
          Email
        </th>
        <th
          scope="col"
          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
        >
          Phone
        </th>
      </>
    )}
    <th
      scope="col"
      className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
    >
      Actions
    </th>
  </tr>
</thead>
<tbody className="bg-white divide-y divide-gray-200">
  {teachers.map((teacher, index) => (
    <tr key={teacher.id}>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {index + 1}
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="text-sm font-medium text-gray-900">
          {teacher.name}
        </div>
      </td>
      {isAdminOrCounsellor && (
        <>
          <td className="px-6 py-4 whitespace-nowrap">
            <div className="text-sm text-gray-500">{teacher.email}</div>
          </td>
          <td className="px-6 py-4 whitespace-nowrap">
            <div className="text-sm text-gray-500">{teacher.phone}</div>
          </td>
        </>
      )}
      <td className="px-6 py-4 whitespace-nowrap text-right">
      <button
  onClick={() => !isTeacher && removeTeacher(teacher.id)}
  className={`text-red-600 ${isTeacher ? 'opacity-40 cursor-not-allowed' : 'hover:text-red-900 cursor-pointer'}`}
  disabled={isTeacher}
  title={isTeacher ? "Teachers cannot remove other teachers" : "Remove teacher"}
>
  <FiTrash2 />
</button>
      </td>
    </tr>
  ))}
</tbody>
        </table>
      </div>
    ) : (
      <div className="bg-gray-50 p-8 rounded-lg text-center">
        <div className="flex justify-center mb-4">
          <div className="p-4 rounded-full bg-gray-200 text-gray-400">
            <FiUser size={24} />
          </div>
        </div>
        <p className="text-gray-600 mb-4">
          No teachers have been assigned to this batch yet.
        </p>
      </div>
    )}
  </div>
)}
      </div>
    </div>
  );
};

export default BatchView;